import { useCallback, useEffect, useMemo, useState } from 'react';
import { createAuditedClient } from '../utils/auditClient';
import { useCountry } from '../context/CountryContext';
import { Button, GhostButton, H3, Input, Panel, Toolbar } from '../ui';

const client = createAuditedClient('PaymentOpsPage');

const InboundByCompanyByCreatedAt = /* GraphQL */ `
  query PaymentOpsInboundByCompanyByCreatedAt(
    $CompanyId: String!
    $createdAt: ModelStringKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelInboundRequestFilterInput
    $limit: Int
  ) {
    inboundByCompanyByCreatedAt(
      CompanyId: $CompanyId
      createdAt: $createdAt
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
    ) {
      items {
        id
        CompanyId
        type
        status
        name
        email
        start
        end
        selectedCarName
        payload
        createdAt
        updatedAt
      }
    }
  }
`;

const InboundByTypeByCreatedAt = /* GraphQL */ `
  query PaymentOpsInboundByTypeByCreatedAt(
    $type: InboundRequestType!
    $createdAt: ModelStringKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelInboundRequestFilterInput
    $limit: Int
  ) {
    inboundByTypeByCreatedAt(
      type: $type
      createdAt: $createdAt
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
    ) {
      items {
        id
        CompanyId
        type
        status
        name
        email
        start
        end
        selectedCarName
        payload
        createdAt
        updatedAt
      }
    }
  }
`;

const ReservationsByCompanyByStart = /* GraphQL */ `
  query PaymentOpsReservationsByCompanyByStart(
    $CompanyId: String!
    $start: ModelStringKeyConditionInput
    $sortDirection: ModelSortDirection
    $limit: Int
  ) {
    reservationsByCompanyByStart(
      CompanyId: $CompanyId
      start: $start
      sortDirection: $sortDirection
      limit: $limit
    ) {
      items {
        id
        reservationRef
        customerName
        customerEmail
        paymentStatus
        paymentAttemptStatus
        paymentOrderId
        paymentTransactionId
        paymentLastVerifiedAt
        paymentFailureReason
        approvalStatus
        amountPaid
        payableNowCurrency
        updatedAt
      }
    }
  }
`;

const ListMpgsReservations = /* GraphQL */ `
  query PaymentOpsListMpgsReservations($filter: ModelReservationFilterInput, $limit: Int) {
    listReservations(filter: $filter, limit: $limit) {
      items {
        id
        CompanyId
        start
        end
        originalCarName
        originalCarPlate
        reservationRef
        customerName
        customerEmail
        paymentStatus
        paymentAttemptStatus
        paymentOrderId
        paymentTransactionId
        paymentLastVerifiedAt
        paymentFailureReason
        approvalStatus
        amountPaid
        payableNow
        payableNowCurrency
        createdAt
        updatedAt
      }
    }
  }
`;

const ListSettledPayoutReservations = /* GraphQL */ `
  query PaymentOpsListSettledPayoutReservations($filter: ModelReservationFilterInput, $limit: Int) {
    listReservations(filter: $filter, limit: $limit) {
      items {
        id
        CompanyId
        start
        end
        originalCarName
        originalCarPlate
        reservationRef
        customerName
        customerEmail
        paymentStatus
        paymentProvider
        paymentAttemptStatus
        paymentOrderId
        paymentTransactionId
        paymentLastVerifiedAt
        paymentFailureReason
        approvalStatus
        amountPaid
        payableNow
        payableNowCurrency
        createdAt
        updatedAt
      }
    }
  }
`;

const LookupMpgsPaymentStatus = /* GraphQL */ `
  query LookupMpgsPaymentStatus($input: LookupMpgsPaymentStatusInput!) {
    lookupMpgsPaymentStatus(input: $input) {
      ok
      paid
      processing
      failed
      confirmed
      appliedNow
      alreadyApplied
      inboundRequestId
      inboundStatus
      reservationId
      reservationRef
      reservationExists
      reservationPaymentStatus
      reservationAttemptStatus
      reservationApprovalStatus
      orderId
      sessionId
      localPaymentStatus
      localPricingPaymentStatus
      paymentStatus
      rawStatus
      gatewayResult
      gatewayCode
      gatewayRecommendation
      gatewayAmount
      gatewayCurrency
      txId
      amountPaid
      currency
      emailSentAt
      lastCheckedAt
      lastError
      customerName
      customerEmail
      message
    }
  }
`;

const ReconcileInboundPayment = /* GraphQL */ `
  mutation ReconcileInboundPayment($input: ReconcileInboundPaymentInput!) {
    reconcileInboundPayment(input: $input) {
      ok
      paid
      processing
      failed
      confirmed
      appliedNow
      alreadyApplied
      inboundRequestId
      inboundStatus
      reservationId
      reservationRef
      reservationExists
      reservationPaymentStatus
      reservationAttemptStatus
      reservationApprovalStatus
      orderId
      sessionId
      localPaymentStatus
      localPricingPaymentStatus
      paymentStatus
      rawStatus
      gatewayResult
      gatewayCode
      gatewayRecommendation
      gatewayAmount
      gatewayCurrency
      txId
      amountPaid
      currency
      emailSentAt
      lastCheckedAt
      lastError
      customerName
      customerEmail
      message
    }
  }
`;

const ListMpgsPaymentAttempts = /* GraphQL */ `
  query ListMpgsPaymentAttempts($input: ListMpgsPaymentAttemptsInput) {
    listMpgsPaymentAttempts(input: $input) {
      inboundRequestId
      CompanyId
      createdAt
      updatedAt
      customerName
      customerEmail
      selectedCarName
      inboundStatus
      orderId
      sessionId
      reservationId
      reservationRef
      reservationExists
      reservationPaymentStatus
      reservationAttemptStatus
      reservationApprovalStatus
      localPaymentStatus
      localPricingPaymentStatus
      amount
      currency
      lastCheckedAt
      lastError
      needsReconciliation
    }
  }
`;

function isoDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString();
}

function safeStr(value) {
  return value == null ? '' : String(value).trim();
}

function parsePayload(value) {
  if (value && typeof value === 'object') return value;
  const raw = safeStr(value);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function formatDateTime(value) {
  if (!value) return '--';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return new Intl.DateTimeFormat('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
}

function formatMoney(amount, currency) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '--';
  const cur = safeStr(currency).toUpperCase();
  return cur ? `${n.toFixed(2)} ${cur}` : n.toFixed(2);
}

function firstFiniteNumber(...values) {
  for (const value of values) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function uniqueById(items) {
  const map = new Map();
  for (const item of items || []) {
    if (!item?.id) continue;
    map.set(String(item.id), item);
  }
  return Array.from(map.values());
}

function isMpgsPaymentAttempt({ payment, pricing, orderId, sessionId }) {
  const provider = safeStr(payment?.provider).toUpperCase();
  const status = safeStr(payment?.status).toUpperCase();
  const pricingStatus = safeStr(pricing?.paymentStatus).toUpperCase();
  if (provider === 'MPGS') return true;
  if (orderId || sessionId || safeStr(payment?.payUrl) || safeStr(payment?.notificationUrl)) return true;
  return ['SESSION_CREATED', 'PENDING', 'PAID', 'PAID_UNAPPLIED', 'FAILED'].includes(status) ||
    ['PENDING', 'PAID', 'PREPAID'].includes(pricingStatus);
}

function badgeStyle(kind = 'neutral') {
  if (kind === 'good') return { background: '#dcfce7', color: '#166534' };
  if (kind === 'warn') return { background: '#fef3c7', color: '#92400e' };
  if (kind === 'bad') return { background: '#fee2e2', color: '#991b1b' };
  if (kind === 'info') return { background: '#dbeafe', color: '#1d4ed8' };
  return { background: '#e5e7eb', color: '#334155' };
}

function Badge({ children, kind = 'neutral' }) {
  const colors = badgeStyle(kind);
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '3px 8px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 800,
        lineHeight: 1.2,
        ...colors,
      }}
    >
      {children}
    </span>
  );
}

function toneForLocalRow(row) {
  if (row.needsReconciliation) return 'bad';
  if (row.reservationExists) return 'good';
  if (row.localPaymentStatus === 'PENDING') return 'warn';
  return 'neutral';
}

function toneForLiveResult(result) {
  if (!result) return 'neutral';
  if (!result.ok) return 'bad';
  if (result.confirmed) return 'good';
  if (result.paid) return 'warn';
  if (result.processing) return 'info';
  if (result.failed) return 'bad';
  return 'neutral';
}

function normalizeRows(inboundItems, reservationMap) {
  return (inboundItems || [])
    .map((item) => {
      const payload = parsePayload(item?.payload);
      const payment = payload?.payment && typeof payload.payment === 'object' ? payload.payment : {};
      const pricing = payload?.pricing && typeof payload.pricing === 'object' ? payload.pricing : {};
      const verify = payment?.verify && typeof payment.verify === 'object' ? payment.verify : {};
      const reservationId = safeStr(payment.reservationId || pricing.reservationId);
      const reservation = reservationId ? reservationMap.get(reservationId) || null : null;
      const orderId = safeStr(payment.orderId || reservation?.paymentOrderId);
      const sessionId = safeStr(payment.sessionId);
      const currency = safeStr(
        payment.currency || pricing.payableNowCurrency || pricing.currency || reservation?.payableNowCurrency
      ).toUpperCase();
      const amount = Number(
        payment.amount ?? payment.amountUsd ?? pricing.payableNow ?? reservation?.amountPaid ?? NaN
      );
      const localPaymentStatus = safeStr(payment.status);
      const localPricingPaymentStatus = safeStr(pricing.paymentStatus);
      const hasPaymentAttempt = isMpgsPaymentAttempt({
        payment,
        pricing,
        orderId,
        sessionId,
      });

      return {
        inboundId: item.id,
        CompanyId: safeStr(item.CompanyId),
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
        customerName: safeStr(item.name || reservation?.customerName) || '--',
        customerEmail: safeStr(item.email || reservation?.customerEmail) || '--',
        selectedCarName: safeStr(item.selectedCarName),
        inboundStatus: safeStr(item.status),
        orderId,
        sessionId,
        reservationId,
        reservationRef: safeStr(reservation?.reservationRef || reservationId),
        reservationExists: !!reservation,
        reservationPaymentStatus: safeStr(reservation?.paymentStatus),
        reservationAttemptStatus: safeStr(reservation?.paymentAttemptStatus),
        reservationApprovalStatus: safeStr(reservation?.approvalStatus),
        localPaymentStatus,
        localPricingPaymentStatus,
        amount: Number.isFinite(amount) ? amount : null,
        currency,
        lastCheckedAt: safeStr(verify.lastCheckedAt || reservation?.paymentLastVerifiedAt),
        lastError: safeStr(verify.applyError || verify.emailError || verify.lastError || reservation?.paymentFailureReason),
        hasPaymentAttempt,
        needsReconciliation:
          Boolean(verify.needsReconciliation) || safeStr(payment.status).toUpperCase() === 'PAID_UNAPPLIED',
      };
    })
    .filter((row) => row.hasPaymentAttempt)
    .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
}

function normalizeAttemptRows(items) {
  return (items || [])
    .filter(Boolean)
    .map((item) => ({
      inboundId: safeStr(item.inboundRequestId),
      CompanyId: safeStr(item.CompanyId),
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      customerName: safeStr(item.customerName) || '--',
      customerEmail: safeStr(item.customerEmail) || '--',
      selectedCarName: safeStr(item.selectedCarName),
      inboundStatus: safeStr(item.inboundStatus),
      orderId: safeStr(item.orderId),
      sessionId: safeStr(item.sessionId),
      reservationId: safeStr(item.reservationId),
      reservationRef: safeStr(item.reservationRef || item.reservationId),
      reservationExists: Boolean(item.reservationExists),
      reservationPaymentStatus: safeStr(item.reservationPaymentStatus),
      reservationAttemptStatus: safeStr(item.reservationAttemptStatus),
      reservationApprovalStatus: safeStr(item.reservationApprovalStatus),
      localPaymentStatus: safeStr(item.localPaymentStatus),
      localPricingPaymentStatus: safeStr(item.localPricingPaymentStatus),
      amount: item.amount == null ? null : Number(item.amount),
      currency: safeStr(item.currency).toUpperCase(),
      lastCheckedAt: safeStr(item.lastCheckedAt),
      lastError: safeStr(item.lastError),
      needsReconciliation: Boolean(item.needsReconciliation),
      hasPaymentAttempt: true,
    }))
    .filter((row) => row.inboundId)
    .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
}

function normalizeSettledReservationRows(items) {
  return (items || [])
    .filter(Boolean)
    .map((item) => {
      const amount = firstFiniteNumber(item.amountPaid, item.payableNow);
      return {
        reservationId: safeStr(item.id),
        CompanyId: safeStr(item.CompanyId),
        paidAt: safeStr(item.paymentLastVerifiedAt || item.updatedAt || item.createdAt),
        start: safeStr(item.start),
        end: safeStr(item.end),
        customerName: safeStr(item.customerName) || '--',
        customerEmail: safeStr(item.customerEmail) || '--',
        selectedCarName: safeStr(item.originalCarName),
        selectedCarPlate: safeStr(item.originalCarPlate),
        reservationRef: safeStr(item.reservationRef || item.id),
        paymentStatus: safeStr(item.paymentStatus),
        paymentProvider: safeStr(item.paymentProvider),
        paymentAttemptStatus: safeStr(item.paymentAttemptStatus),
        paymentOrderId: safeStr(item.paymentOrderId),
        paymentTransactionId: safeStr(item.paymentTransactionId),
        approvalStatus: safeStr(item.approvalStatus),
        amount,
        currency: safeStr(item.payableNowCurrency).toUpperCase(),
        lastError: safeStr(item.paymentFailureReason),
      };
    })
    .filter((row) => row.reservationId && row.amount != null && row.amount > 0)
    .sort((a, b) => new Date(b.paidAt || b.start || 0).getTime() - new Date(a.paidAt || a.start || 0).getTime());
}

function summarizeMoneyByCurrency(rows) {
  const map = new Map();
  for (const row of rows || []) {
    const amount = Number(row.amount);
    if (!Number.isFinite(amount)) continue;
    const currency = safeStr(row.currency) || 'N/A';
    map.set(currency, (map.get(currency) || 0) + amount);
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([currency, amount]) => formatMoney(amount, currency))
    .join(' | ');
}

export default function PaymentOpsPage() {
  const { selectedCompanyId, loadingCompany, companyError, groups } = useCountry();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [liveById, setLiveById] = useState({});
  const [busyById, setBusyById] = useState({});
  const [bulkBusy, setBulkBusy] = useState(false);
  const [lastLoadedAt, setLastLoadedAt] = useState('');
  const [scope, setScope] = useState('all');
  const [loadMeta, setLoadMeta] = useState({ loaded: 0, scoped: 0, tracked: 0 });
  const [settledRows, setSettledRows] = useState([]);
  const [settledError, setSettledError] = useState('');
  const [settledMeta, setSettledMeta] = useState({ loaded: 0, candidates: 0 });

  const canShowAllCompanies = groups?.includes?.('admin');
  const effectiveScope = canShowAllCompanies ? scope : 'selected';

  const setBusy = useCallback((key, value) => {
    setBusyById((prev) => ({ ...prev, [key]: value }));
  }, []);

  const load = useCallback(async () => {
    if (effectiveScope === 'selected' && loadingCompany) return;
    if (effectiveScope === 'selected' && !selectedCompanyId) {
      setRows([]);
      setSettledRows([]);
      setLoadMeta({ loaded: 0, scoped: 0, tracked: 0 });
      setSettledMeta({ loaded: 0, candidates: 0 });
      setLoading(false);
      setError(companyError || 'No company selected.');
      return;
    }

    setLoading(true);
    setError('');
    setSettledError('');

    try {
      let loadedAttemptRows = false;
      try {
        const input = { days: 120, limit: 150 };
        if (effectiveScope === 'selected' && selectedCompanyId) {
          input.companyId = selectedCompanyId;
        }

        const listRes = await client.graphql({
          query: ListMpgsPaymentAttempts,
          variables: { input },
          authMode: 'userPool',
        });
        const listed = listRes?.data?.listMpgsPaymentAttempts;
        if (Array.isArray(listed)) {
          const nextRows = normalizeAttemptRows(listed);
          setRows(nextRows);
          setLoadMeta({
            loaded: listed.length,
            scoped: listed.length,
            tracked: nextRows.length,
          });
          loadedAttemptRows = true;
        }
      } catch (listErr) {
        console.warn('PaymentOpsPage server payment list failed; falling back to model queries', listErr);
      }

      if (!loadedAttemptRows) {
        const typeFilter =
          effectiveScope === 'selected' && selectedCompanyId
            ? { CompanyId: { eq: selectedCompanyId } }
            : undefined;

        const inboundQueries = [
          client.graphql({
            query: InboundByTypeByCreatedAt,
            variables: {
              type: 'PUBLIC_RESERVATION',
              createdAt: { ge: isoDaysAgo(90) },
              sortDirection: 'DESC',
              filter: typeFilter,
              limit: 250,
            },
            authMode: 'userPool',
          }),
        ];

        if (selectedCompanyId) {
          inboundQueries.push(
            client.graphql({
              query: InboundByCompanyByCreatedAt,
              variables: {
                CompanyId: selectedCompanyId,
                createdAt: { ge: isoDaysAgo(90) },
                sortDirection: 'DESC',
                filter: { type: { eq: 'PUBLIC_RESERVATION' } },
                limit: 150,
              },
              authMode: 'userPool',
            })
          );
        }

        const reservationQuery =
          effectiveScope === 'selected' && selectedCompanyId
            ? client.graphql({
                query: ReservationsByCompanyByStart,
                variables: {
                  CompanyId: selectedCompanyId,
                  start: { ge: isoDaysAgo(120) },
                  sortDirection: 'DESC',
                  limit: 400,
                },
                authMode: 'userPool',
              })
            : client.graphql({
                query: ListMpgsReservations,
                variables: {
                  filter: { paymentProvider: { eq: 'MPGS' } },
                  limit: 500,
                },
                authMode: 'userPool',
              });

        const [inboundResults, reservationRes] = await Promise.all([
          Promise.all(inboundQueries),
          reservationQuery,
        ]);

        const inboundItems = uniqueById(
          inboundResults.flatMap((res) => [
            ...(res?.data?.inboundByTypeByCreatedAt?.items || []),
            ...(res?.data?.inboundByCompanyByCreatedAt?.items || []),
          ])
        );

        const scopedInboundItems =
          effectiveScope === 'selected' && selectedCompanyId
            ? inboundItems.filter((item) => safeStr(item.CompanyId) === safeStr(selectedCompanyId))
            : inboundItems;

        const reservationItems = [
          ...(reservationRes?.data?.reservationsByCompanyByStart?.items || []),
          ...(reservationRes?.data?.listReservations?.items || []),
        ].filter(Boolean);

        const reservationMap = new Map(
          reservationItems.map((row) => [String(row.id), row])
        );

        const nextRows = normalizeRows(
          scopedInboundItems,
          reservationMap
        );

        setRows(nextRows);
        setLoadMeta({
          loaded: inboundItems.length,
          scoped: scopedInboundItems.length,
          tracked: nextRows.length,
        });
      }

      try {
        const settledFilter = {
          paymentProvider: { eq: 'MPGS' },
          paymentStatus: { eq: 'PREPAID' },
        };
        if (effectiveScope === 'selected' && selectedCompanyId) {
          settledFilter.CompanyId = { eq: selectedCompanyId };
        }

        const settledRes = await client.graphql({
          query: ListSettledPayoutReservations,
          variables: {
            filter: settledFilter,
            limit: 1000,
          },
          authMode: 'userPool',
        });
        const settledItems = (settledRes?.data?.listReservations?.items || []).filter(Boolean);
        const nextSettledRows = normalizeSettledReservationRows(settledItems);
        setSettledRows(nextSettledRows);
        setSettledMeta({
          loaded: settledItems.length,
          candidates: nextSettledRows.length,
        });
      } catch (settledErr) {
        console.warn('PaymentOpsPage settled reservation list failed', settledErr);
        setSettledRows([]);
        setSettledMeta({ loaded: 0, candidates: 0 });
        setSettledError(String(settledErr?.errors?.[0]?.message || settledErr?.message || settledErr));
      }

      setLastLoadedAt(new Date().toISOString());
    } catch (err) {
      console.error('PaymentOpsPage load failed', err);
      setError(String(err?.errors?.[0]?.message || err?.message || err));
      setRows([]);
      setSettledRows([]);
      setLoadMeta({ loaded: 0, scoped: 0, tracked: 0 });
      setSettledMeta({ loaded: 0, candidates: 0 });
    } finally {
      setLoading(false);
    }
  }, [companyError, effectiveScope, loadingCompany, selectedCompanyId]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredRows = useMemo(() => {
    const q = safeStr(search).toLowerCase();
    if (!q) return rows;
    return rows.filter((row) =>
      [
        row.inboundId,
        row.CompanyId,
        row.customerName,
        row.customerEmail,
        row.orderId,
        row.reservationId,
        row.reservationRef,
        row.selectedCarName,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q))
    );
  }, [rows, search]);

  const filteredSettledRows = useMemo(() => {
    const q = safeStr(search).toLowerCase();
    if (!q) return settledRows;
    return settledRows.filter((row) =>
      [
        row.reservationId,
        row.CompanyId,
        row.customerName,
        row.customerEmail,
        row.paymentOrderId,
        row.paymentTransactionId,
        row.reservationRef,
        row.selectedCarName,
        row.selectedCarPlate,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q))
    );
  }, [settledRows, search]);

  const summary = useMemo(() => {
    const localAttention = rows.filter((row) => row.needsReconciliation).length;
    const locallyConfirmed = rows.filter((row) => row.reservationExists).length;
    const liveAttention = Object.values(liveById).filter((result) => result?.paid && !result?.confirmed).length;
    return {
      total: rows.length,
      locallyConfirmed,
      localAttention,
      liveAttention,
      settledCandidates: settledRows.length,
      settledTotal: summarizeMoneyByCurrency(settledRows),
    };
  }, [liveById, rows, settledRows]);

  const lookupLiveStatus = useCallback(async (inboundRequestId) => {
    const busyKey = `lookup:${inboundRequestId}`;
    setBusy(busyKey, true);
    try {
      const res = await client.graphql({
        query: LookupMpgsPaymentStatus,
        variables: { input: { inboundRequestId } },
        authMode: 'userPool',
      });
      const result = res?.data?.lookupMpgsPaymentStatus || null;
      setLiveById((prev) => ({ ...prev, [inboundRequestId]: result }));
      return result;
    } catch (err) {
      console.error('lookupMpgsPaymentStatus failed', err);
      const fallback = {
        ok: false,
        inboundRequestId,
        message: String(err?.errors?.[0]?.message || err?.message || err),
      };
      setLiveById((prev) => ({ ...prev, [inboundRequestId]: fallback }));
      return fallback;
    } finally {
      setBusy(busyKey, false);
    }
  }, [setBusy]);

  const reconcilePayment = useCallback(async (inboundRequestId) => {
    const busyKey = `reconcile:${inboundRequestId}`;
    setBusy(busyKey, true);
    try {
      const res = await client.graphql({
        query: ReconcileInboundPayment,
        variables: { input: { inboundRequestId } },
        authMode: 'userPool',
      });
      const result = res?.data?.reconcileInboundPayment || null;
      setLiveById((prev) => ({ ...prev, [inboundRequestId]: result }));
      await load();
      return result;
    } catch (err) {
      console.error('reconcileInboundPayment failed', err);
      const fallback = {
        ok: false,
        inboundRequestId,
        message: String(err?.errors?.[0]?.message || err?.message || err),
      };
      setLiveById((prev) => ({ ...prev, [inboundRequestId]: fallback }));
      return fallback;
    } finally {
      setBusy(busyKey, false);
    }
  }, [load, setBusy]);

  const refreshVisibleStatuses = useCallback(async () => {
    const targets = filteredRows.filter((row) => row.orderId).slice(0, 25);
    if (!targets.length) return;
    setBulkBusy(true);
    try {
      for (const row of targets) {
        // sequential to avoid hammering the resolver/gateway
        // eslint-disable-next-line no-await-in-loop
        await lookupLiveStatus(row.inboundId);
      }
    } finally {
      setBulkBusy(false);
    }
  }, [filteredRows, lookupLiveStatus]);

  return (
    <div style={{ padding: 16, display: 'grid', gap: 12 }}>
      <div>
        <H3 style={{ marginBottom: 4 }}>Payment Ops</H3>
        <div style={{ color: '#6b7280', fontSize: 14 }}>
          Recent MPGS-backed reservation payments with live gateway status and manual reconcile.
        </div>
      </div>

      <Toolbar>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search ref, order ID, customer, email, car…"
          style={{ minWidth: 260, maxWidth: 420 }}
        />
        {canShowAllCompanies ? (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <GhostButton type="button" onClick={() => setScope('selected')} disabled={scope === 'selected'}>
              Selected company
            </GhostButton>
            <GhostButton type="button" onClick={() => setScope('all')} disabled={scope === 'all'}>
              All companies
            </GhostButton>
          </div>
        ) : null}
        <Button
          type="button"
          onClick={load}
          disabled={loading || (effectiveScope === 'selected' && loadingCompany)}
        >
          {loading ? 'Loading…' : 'Reload local data'}
        </Button>
        <GhostButton type="button" onClick={refreshVisibleStatuses} disabled={bulkBusy || !filteredRows.length}>
          {bulkBusy ? 'Checking…' : 'Check visible live status'}
        </GhostButton>
        <div style={{ color: '#6b7280', fontSize: 12 }}>
          Last local refresh: {lastLoadedAt ? formatDateTime(lastLoadedAt) : '--'}
          {' '}| loaded {loadMeta.loaded}, scoped {loadMeta.scoped}, tracked {loadMeta.tracked}
        </div>
      </Toolbar>

      <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
        <Panel title="Tracked orders">
          <div style={{ fontSize: 28, fontWeight: 900 }}>{summary.total}</div>
        </Panel>
        <Panel title="Locally confirmed">
          <div style={{ fontSize: 28, fontWeight: 900, color: '#166534' }}>{summary.locallyConfirmed}</div>
        </Panel>
        <Panel title="Needs local attention">
          <div style={{ fontSize: 28, fontWeight: 900, color: summary.localAttention ? '#b91c1c' : '#166534' }}>
            {summary.localAttention}
          </div>
        </Panel>
        <Panel title="Live paid, not confirmed">
          <div style={{ fontSize: 28, fontWeight: 900, color: summary.liveAttention ? '#b91c1c' : '#166534' }}>
            {summary.liveAttention}
          </div>
        </Panel>
        <Panel title="Transfer candidates">
          <div style={{ fontSize: 28, fontWeight: 900, color: summary.settledCandidates ? '#166534' : '#111827' }}>
            {summary.settledCandidates}
          </div>
          <div style={{ marginTop: 4, color: '#6b7280', fontSize: 12 }}>
            {summary.settledTotal || '--'}
          </div>
        </Panel>
      </div>

      <Panel
        title="Settled reservation transfer candidates"
        right={
          <div style={{ color: '#6b7280', fontSize: 12 }}>
            loaded {settledMeta.loaded}, candidates {settledMeta.candidates}
          </div>
        }
      >
        {settledError ? (
          <div style={{ color: '#991b1b', marginBottom: 12 }}>{settledError}</div>
        ) : null}

        {loading ? (
          <div style={{ color: '#6b7280' }}>Loading settled reservations...</div>
        ) : !filteredSettledRows.length ? (
          <div style={{ color: '#6b7280' }}>No confirmed MPGS reservations found for this scope.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1080 }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>
                  <th style={{ padding: '10px 8px' }}>Paid</th>
                  <th style={{ padding: '10px 8px' }}>Customer</th>
                  <th style={{ padding: '10px 8px' }}>Reservation</th>
                  <th style={{ padding: '10px 8px' }}>Amount</th>
                  <th style={{ padding: '10px 8px' }}>Status</th>
                  <th style={{ padding: '10px 8px' }}>Gateway</th>
                </tr>
              </thead>
              <tbody>
                {filteredSettledRows.map((row) => (
                  <tr key={row.reservationId} style={{ borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' }}>
                    <td style={{ padding: '12px 8px', fontSize: 13, color: '#475569', whiteSpace: 'nowrap' }}>
                      {formatDateTime(row.paidAt)}
                    </td>

                    <td style={{ padding: '12px 8px', fontSize: 13 }}>
                      <div style={{ fontWeight: 800, color: '#111827' }}>{row.customerName}</div>
                      <div style={{ color: '#6b7280', marginTop: 4 }}>{row.customerEmail}</div>
                      {row.selectedCarName ? (
                        <div style={{ color: '#475569', marginTop: 6 }}>
                          {row.selectedCarName}{row.selectedCarPlate ? ` (${row.selectedCarPlate})` : ''}
                        </div>
                      ) : null}
                    </td>

                    <td style={{ padding: '12px 8px', fontSize: 12, color: '#334155' }}>
                      <div><b>Company:</b> {row.CompanyId || '--'}</div>
                      <div><b>Reservation:</b> {row.reservationRef || '--'}</div>
                      <div style={{ marginTop: 4 }}><b>ID:</b> {row.reservationId}</div>
                      <div style={{ marginTop: 4 }}><b>Trip:</b> {formatDateTime(row.start)} - {formatDateTime(row.end)}</div>
                    </td>

                    <td style={{ padding: '12px 8px', fontSize: 13, color: '#111827', whiteSpace: 'nowrap' }}>
                      {formatMoney(row.amount, row.currency)}
                    </td>

                    <td style={{ padding: '12px 8px', fontSize: 12 }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        <Badge kind="good">{row.paymentStatus || 'PREPAID'}</Badge>
                        {row.paymentAttemptStatus ? <Badge kind="good">{row.paymentAttemptStatus}</Badge> : null}
                        {row.approvalStatus ? <Badge kind="neutral">{row.approvalStatus}</Badge> : null}
                      </div>
                      {row.lastError ? (
                        <div style={{ color: '#991b1b', marginTop: 8, maxWidth: 280 }}>{row.lastError}</div>
                      ) : null}
                    </td>

                    <td style={{ padding: '12px 8px', fontSize: 12, color: '#334155' }}>
                      <div><b>Provider:</b> {row.paymentProvider || '--'}</div>
                      <div style={{ marginTop: 4 }}><b>Order:</b> {row.paymentOrderId || '--'}</div>
                      <div style={{ marginTop: 4 }}><b>Tx:</b> {row.paymentTransactionId || '--'}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel
        title="Recent MPGS payment attempts"
        right={
          <div style={{ color: '#6b7280', fontSize: 12 }}>
            Local = Haste data. Live = direct MPGS lookup from the backend.
          </div>
        }
      >
        {error ? (
          <div style={{ color: '#991b1b', marginBottom: 12 }}>{error}</div>
        ) : null}

        {loading ? (
          <div style={{ color: '#6b7280' }}>Loading recent payment attempts…</div>
        ) : !filteredRows.length ? (
          <div style={{ color: '#6b7280' }}>No recent MPGS payment attempts found for this company.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1220 }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>
                  <th style={{ padding: '10px 8px' }}>Created</th>
                  <th style={{ padding: '10px 8px' }}>Customer</th>
                  <th style={{ padding: '10px 8px' }}>Refs</th>
                  <th style={{ padding: '10px 8px' }}>Amount</th>
                  <th style={{ padding: '10px 8px' }}>Local</th>
                  <th style={{ padding: '10px 8px' }}>Live MPGS</th>
                  <th style={{ padding: '10px 8px' }}>Last check</th>
                  <th style={{ padding: '10px 8px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => {
                  const live = liveById[row.inboundId];
                  const localTone = toneForLocalRow(row);
                  const liveTone = toneForLiveResult(live);
                  const lookupBusy = Boolean(busyById[`lookup:${row.inboundId}`]);
                  const reconcileBusy = Boolean(busyById[`reconcile:${row.inboundId}`]);
                  const canReconcile =
                    Boolean(row.orderId) &&
                    (
                      live
                        ? Boolean(live.paid && !live.confirmed)
                        : Boolean(row.needsReconciliation || !row.reservationExists)
                    );

                  return (
                    <tr key={row.inboundId} style={{ borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' }}>
                      <td style={{ padding: '12px 8px', fontSize: 13, color: '#475569' }}>
                        <div>{formatDateTime(row.createdAt)}</div>
                        <div style={{ marginTop: 6 }}>
                          <Badge kind="info">{row.inboundStatus || 'NEW'}</Badge>
                        </div>
                      </td>

                      <td style={{ padding: '12px 8px', fontSize: 13 }}>
                        <div style={{ fontWeight: 800, color: '#111827' }}>{row.customerName}</div>
                        <div style={{ color: '#6b7280', marginTop: 4 }}>{row.customerEmail}</div>
                        {row.selectedCarName ? (
                          <div style={{ color: '#475569', marginTop: 6 }}>{row.selectedCarName}</div>
                        ) : null}
                      </td>

                      <td style={{ padding: '12px 8px', fontSize: 12, color: '#334155' }}>
                        <div><b>Company:</b> {row.CompanyId || '--'}</div>
                        <div><b>Inbound:</b> {row.inboundId}</div>
                        <div style={{ marginTop: 4 }}><b>Order:</b> {row.orderId || '--'}</div>
                        <div style={{ marginTop: 4 }}><b>Reservation:</b> {row.reservationRef || '--'}</div>
                      </td>

                      <td style={{ padding: '12px 8px', fontSize: 13, color: '#111827', whiteSpace: 'nowrap' }}>
                        {row.amount != null ? formatMoney(row.amount, row.currency) : '--'}
                      </td>

                      <td style={{ padding: '12px 8px', fontSize: 12 }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          <Badge kind={localTone}>{row.localPaymentStatus || 'NO_LOCAL_STATUS'}</Badge>
                          {row.localPricingPaymentStatus ? (
                            <Badge kind={row.localPricingPaymentStatus === 'PAID' ? 'good' : 'neutral'}>
                              pricing: {row.localPricingPaymentStatus}
                            </Badge>
                          ) : null}
                          {row.reservationPaymentStatus ? (
                            <Badge kind={row.reservationExists ? 'good' : 'neutral'}>
                              reservation: {row.reservationPaymentStatus}
                            </Badge>
                          ) : null}
                          {row.reservationAttemptStatus ? (
                            <Badge kind="neutral">{row.reservationAttemptStatus}</Badge>
                          ) : null}
                        </div>
                        {row.lastError ? (
                          <div style={{ color: '#991b1b', marginTop: 8, maxWidth: 240 }}>{row.lastError}</div>
                        ) : null}
                      </td>

                      <td style={{ padding: '12px 8px', fontSize: 12 }}>
                        {live ? (
                          <div style={{ display: 'grid', gap: 6 }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                              <Badge kind={liveTone}>
                                {live.confirmed
                                  ? 'CONFIRMED'
                                  : live.paid
                                    ? 'PAID_NOT_APPLIED'
                                    : live.processing
                                      ? 'PROCESSING'
                                      : live.failed
                                        ? 'FAILED'
                                        : live.ok
                                          ? 'CHECKED'
                                          : 'ERROR'}
                              </Badge>
                              {live.rawStatus ? <Badge kind="neutral">{live.rawStatus}</Badge> : null}
                              {live.gatewayCode ? <Badge kind="neutral">{live.gatewayCode}</Badge> : null}
                            </div>
                            <div style={{ color: '#475569' }}>{live.message || '--'}</div>
                          </div>
                        ) : (
                          <div style={{ color: '#6b7280' }}>Not checked yet</div>
                        )}
                      </td>

                      <td style={{ padding: '12px 8px', fontSize: 12, color: '#475569' }}>
                        {formatDateTime(live?.lastCheckedAt || row.lastCheckedAt)}
                      </td>

                      <td style={{ padding: '12px 8px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 150 }}>
                          <Button
                            type="button"
                            onClick={() => lookupLiveStatus(row.inboundId)}
                            disabled={!row.orderId || lookupBusy || reconcileBusy}
                            style={{ width: '100%' }}
                          >
                            {!row.orderId ? 'No MPGS order yet' : lookupBusy ? 'Checking…' : 'Check live'}
                          </Button>
                          <GhostButton
                            type="button"
                            onClick={() => reconcilePayment(row.inboundId)}
                            disabled={!canReconcile || lookupBusy || reconcileBusy}
                            style={{ width: '100%' }}
                          >
                            {reconcileBusy ? 'Reconciling…' : 'Reconcile'}
                          </GhostButton>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
